import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { invoiceActions } from '../../../sagas/invoiceSlice';
import { reglementActions } from '../../../sagas/reglementSlice';
import './InvoiceList.css';
import { clientActions } from '../../../sagas/clientSlice';
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Modal,
    ModalBody,
    ModalHeader,
    ModalFooter,
    Pagination,
    PaginationItem,
    PaginationLink,
    Popover,
    PopoverBody,
    Row,
    Spinner,
    Input,
    FormGroup,
    Label,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';
import {
    FaFileInvoice,
    FaFilePdf,
    FaSearch,
    FaMoneyBillWave,
    FaClock,
    FaPlus,
    FaCopy,
    FaCheck,
    FaTimes,
    FaEdit,
    FaEye,
    FaTrash,
    FaEllipsisV,
    FaCheckCircle,
    FaExclamationTriangle,
    FaTimesCircle,
    FaFilter,
    FaAngleUp,
    FaAngleDown
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPrice, convertNumberToWords } from '../../../helpers/utility';
import SubInvoiceManager from '../SubInvoiceManager';
import { productActions } from '../../../sagas/productSlice';
import InvoiceForm from '../InvoiceForm';
import ViewFacture from '../ViewFacture';
import { generateDoc } from '../../../helpers/generateDoc';
import Select from "react-select";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FaFileExcel } from 'react-icons/fa';

const InvoiceList = () => {
    const dispatch = useDispatch();
    const { invoices, loading, subInvoices, totals, totalPages, totalElements } = useSelector((state) => state.invoices);
    const { t } = useTranslation();
    const { company } = useSelector(state => state.auth);
    const searchInputRef = useRef(null);

    // États pour la gestion des sous-factures
    const [showSubInvoiceModal, setShowSubInvoiceModal] = useState(false);
    const [selectedInvoiceForSubInvoices, setSelectedInvoiceForSubInvoices] = useState(null);
    const now = new Date();
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const getMonthDateRange = (monthValue) => {
        if (!monthValue) {
            return { dateFrom: '', dateTo: '' };
        }

        const [year, month] = monthValue.split('-').map(Number);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);

        return {
            dateFrom: formatDate(firstDay),
            dateTo: formatDate(lastDay),
        };
    };
    const [showInvoiceModal, setShowInvoiceModal] = useState(false); // State to control the invoice modal
    const [invoice, setInvoice] = useState({
        lignes: [],
        dateFacture: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        dateEcheance: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        statut: 'IMPAYE'
    }); // State to hold the invoice object
    const clients = useSelector(state => state.clients.clients);
    const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unites

    // États pour l'édition des factures
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState(null);
    const [showPrintSettingsModal, setShowPrintSettingsModal] = useState(false);
    const [invoiceToPrint, setInvoiceToPrint] = useState(null);
    const [printPreviewUrl, setPrintPreviewUrl] = useState(null);
    const [showPaymentsModal, setShowPaymentsModal] = useState(false);
    const [selectedInvoiceForPayments, setSelectedInvoiceForPayments] = useState(null);
    const [paymentsByInvoiceKey, setPaymentsByInvoiceKey] = useState({});
    const [paymentsLoadingKey, setPaymentsLoadingKey] = useState(null);
    const [paymentsError, setPaymentsError] = useState(null);
    const [printSettings, setPrintSettings] = useState({
        scale: 100,
        offsetX: 0,
        offsetY: 0,
        showPaymentDetails: true,
    });
    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: formatDate(startOfCurrentMonth),
        dateTo: formatDate(tomorrowDate),
        amount: null,
        query: '',
        month: ''
    });
    const [amountInput, setAmountInput] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [sortConfig, setSortConfig] = useState({
        key: 'dateFacture',
        direction: 'desc'
    });

    const fetchInvoicesData = useCallback((payload) => {
        dispatch(invoiceActions.listWithSubInvoices(payload));
        const { page, size, ...totalsPayload } = payload || {};
        dispatch(invoiceActions.totals(totalsPayload));
    }, [dispatch]);

    const totalTtcSummary = totals?.totalTTC ?? totals?.montantTotalTTC ?? totals?.totalAmountTtc ?? 0;
    const totalRegleSummary = totals?.totalPaye ?? totals?.montantRegle ?? totals?.montantTotalPaye ?? 0;
    const getInvoicePaymentKey = useCallback((invoice) => invoice?.id ?? invoice?.numero ?? '', []);
    const selectedInvoicePaymentKey = getInvoicePaymentKey(selectedInvoiceForPayments);
    const selectedInvoicePayments = selectedInvoicePaymentKey
        ? paymentsByInvoiceKey[selectedInvoicePaymentKey] || []
        : [];
    const selectedInvoicePaymentsTotal = selectedInvoicePayments.reduce(
        (sum, payment) => sum + Number(payment?.montantAlloue ?? payment?.montant ?? payment?.montantRegle ?? 0),
        0
    );
    const selectedInvoiceRemainingAmount = Math.max(
        Number(selectedInvoiceForPayments?.montantTotalTTC || 0) - selectedInvoicePaymentsTotal,
        0
    );

    const formatDateFr = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
    };

    const getPaymentModeLabel = (mode) => {
        switch (mode) {
            case 'ESPECES':
                return t('facture.modeReglement.espece', 'Espèces');
            case 'CHEQUE':
                return t('facture.modeReglement.cheque', 'Chèque');
            case 'VIREMENT':
                return t('facture.modeReglement.virement', 'Virement');
            case 'TRAITE':
                return t('facture.modeReglement.traite', 'Traite');
            case 'CARTE_BANCAIRE':
                return t('facture.modeReglement.card', 'Carte bancaire');
            default:
                return mode || '-';
        }
    };

    const normalizePaymentsResponse = useCallback((data) => {
        const body = data?.body ?? data?.data?.body ?? data?.data ?? data;
        if (Array.isArray(body)) return body;
        if (Array.isArray(body?.content)) return body.content;
        if (Array.isArray(body?.reglements)) return body.reglements;
        if (Array.isArray(body?.allocations)) return body.allocations;
        return [];
    }, []);

    const fetchPaymentsByNumero = useCallback((numero) => new Promise((resolve) => {
        dispatch(reglementActions.getReglementsByInvoiceRequest({
            numero,
            onSuccess: (data) => resolve(normalizePaymentsResponse(data)),
            onError: () => resolve([]),
        }));
    }), [dispatch, normalizePaymentsResponse]);

    const loadInvoicePayments = useCallback(async (invoice, { force = false } = {}) => {
        const key = getInvoicePaymentKey(invoice);
        if (!key) return [];

        if (!force && paymentsByInvoiceKey[key]) {
            return paymentsByInvoiceKey[key];
        }

        if (!invoice?.numero) {
            setPaymentsByInvoiceKey((prev) => ({ ...prev, [key]: [] }));
            return [];
        }

        setPaymentsLoadingKey(key);
        setPaymentsError(null);

        try {
            const payments = await fetchPaymentsByNumero(invoice.numero);
            setPaymentsByInvoiceKey((prev) => ({ ...prev, [key]: payments }));
            return payments;
        } catch (error) {
            setPaymentsError(error);
            setPaymentsByInvoiceKey((prev) => ({ ...prev, [key]: [] }));
            return [];
        } finally {
            setPaymentsLoadingKey((currentKey) => (currentKey === key ? null : currentKey));
        }
    }, [
        fetchPaymentsByNumero,
        getInvoicePaymentKey,
        paymentsByInvoiceKey,
    ]);

    const openPaymentsModal = (invoice) => {
        setSelectedInvoiceForPayments(invoice);
        setShowPaymentsModal(true);
        loadInvoicePayments(invoice, { force: true });
    };

    const closePaymentsModal = () => {
        setShowPaymentsModal(false);
        setSelectedInvoiceForPayments(null);
    };

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [invoices]);


    useEffect(() => {
        dispatch(productActions.findAllUnites());
        dispatch(clientActions.search({ query: "" }));
    }, []);

    useEffect(() => {
        fetchInvoicesData({
            ...filters,
            page: currentPage,
            size: pageSize
        });
    }, [filters.clientId, filters.dateFrom, filters.dateTo, filters.amount, filters.month, currentPage, pageSize, fetchInvoicesData]);

    const debouncedSearch = () => {
        setCurrentPage(0);
        fetchInvoicesData({
            ...filters,
            page: 0,
            size: pageSize
        });
    }

    const handleExportInvoicesExcel = () => {
        if (!invoices || invoices.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning') || 'Attention',
                text: t('facture.noInvoices') || 'Aucune facture à exporter'
            });
            return;
        }

        // Prepare data
        const data = invoices.map(inv => ({
            "N° Facture": inv.numero || '',
            "Client": inv.clientNom || '',
            "Date facture": inv.dateFacture || '',
            "Mode règlement": inv.modeReglement || '',
            "Statut": inv.statut || '',
            "Total HT": inv.montantTotalHT || 0,
            "TVA": inv.montantTotalTaxe || 0,
            "Total TTC": inv.montantTotalTTC || 0,
            "Montant réglé": inv.montantRegle || 0,
            "BL associés": inv.bonLivraionsRefs || ''
        }));

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(data);

        // Auto width columns
        const colWidths = Object.keys(data[0]).map(key => ({
            wch: Math.max(
                key.length,
                ...data.map(row => String(row[key] ?? '').length)
            )
        }));
        worksheet['!cols'] = colWidths;

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Factures');

        // Export
        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array'
        });

        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const filename = `Factures_${filters.dateFrom || ''}_${filters.dateTo || ''}.xlsx`;
        saveAs(blob, filename);
    };



    const clientOptions = clients?.map(client => ({
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers || []
    })) ?? [];

    const clientOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...clientOptions
    ];

    // Fonction pour ouvrir le modal d'édition
    const handleEditInvoice = (invoice) => {
        setSelectedInvoiceForEdit(invoice);
        setShowEditModal(true);
    };

    const handleAnnulerFacture = (facture) => {
        Swal.fire({
            title: t('facture.deleteConfirmTitle'),
            text: t('facture.deleteConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    let payload = {
                        id: facture.id,
                        motifAnnulation:"",
                        onSuccess: () => {
                            Swal.fire(t('facture.deleted'), '', 'success');
                            debouncedSearch();
                        },
                        onError: (error) => {
                            Swal.fire({
                                icon: 'error',
                                title: t('delivery.deleteError'),
                                text: t('delivery.deleteErrorMessage')
                            });
                        }
                    }
                    dispatch(invoiceActions.cancelInvoiceRequest(payload));

                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: t('common.error'),
                        text: t('delivery.deleteError')
                    });
                }
            }
        });
    }

    // Fonction pour fermer le modal d'édition
    const handleCloseEditModal = () => {
        fetchInvoicesData({
            ...filters,
            page: currentPage,
            size: pageSize
        });
        setShowEditModal(false);
        setSelectedInvoiceForEdit(null);
    };

    // Fonction pour sauvegarder les modifications
    const handleSaveInvoice = () => {
        handleCloseEditModal();
    };

    // Créer les sous-factures
    const handleSubInvoicesCreation = (newSubInvoices) => {
        const payload = {
            parentInvoiceId: selectedInvoiceForSubInvoices.id,
            subInvoices: newSubInvoices.map(sub => ({
                numero: sub.numero,
                montantTTC: sub.montant,
                description: sub.description,
                clientId: selectedInvoiceForSubInvoices.clientId,
                lignes: calculateSubInvoiceLines(selectedInvoiceForSubInvoices, sub.montant)
            }))
        };

        dispatch(invoiceActions.createSubInvoices({
            ...payload,
            onSuccess: (createdSubInvoices) => {
                Swal.fire({
                    icon: 'success',
                    title: t('subInvoice.created'),
                    text: t('subInvoice.createdMessage', { count: newSubInvoices.length }),
                    showConfirmButton: false,
                    timer: 2000
                });

                setShowSubInvoiceModal(false);
                setSelectedInvoiceForSubInvoices(null);
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('subInvoice.creationError'),
                    text: error.message || t('subInvoice.creationErrorMessage')
                });
            }
        }));
    };

    // Calculer les lignes pour une sous-facture (proportionnellement)
    const calculateSubInvoiceLines = (parentInvoice, subInvoiceAmount) => {
        const ratio = subInvoiceAmount / parentInvoice.montantTotalTTC;

        return parentInvoice.lignes.map(ligne => ({
            ...ligne,
            quantite: ligne.quantite * ratio,
            montantHT: ligne.montantHT * ratio,
            montantTTC: ligne.montantTTC * ratio,
            montantTaxe: ligne.montantTaxe * ratio
        }));
    };

    // Obtenir les sous-factures d'une facture
    const getSubInvoicesForInvoice = (invoiceId) => {
        return subInvoices[invoiceId] || [];
    };

    // Fonction pour déterminer si une facture peut être éditée
    const canEditInvoice = (invoice) => {
        const invoiceSubInvoices = getSubInvoicesForInvoice(invoice.id);
        return invoice.statut !== 'PAYEE' &&
            invoice.statut !== 'ANNULEE' &&
            invoiceSubInvoices.length === 0;
    };

    // Fonction pour déterminer si une facture peut être supprimée
    const canDeleteInvoice = (invoice) => {
        const invoiceSubInvoices = getSubInvoicesForInvoice(invoice.id);
        return invoice.statut !== 'PAYEE' &&
            invoiceSubInvoices.length === 0;
    };

    // Fonction pour générer le PDF
    const buildInvoicePdfPayload = useCallback((invoice, settings = printSettings, reglements = []) => ({
        matjarDoc: {
            ...invoice,
            lignes: invoice.lignes.map(l => ({
                ...l,
                unite: unites.find(u => u.code === l.unite)?.libelle
            }))
        },
        sellerModel: company,
        priceMode: "HT",
        reglements,
        showPaymentDetails: settings?.showPaymentDetails !== false,
        contentScale: (settings?.scale || 100) / 100,
        offsetX: settings?.offsetX || 0,
        offsetY: settings?.offsetY || 0,
    }), [company, printSettings, unites]);

    const handleGeneratePdf = async (invoice, settings = printSettings) => {
        const reglements = await loadInvoicePayments(invoice);
        generateDoc("facture", {
            ...buildInvoicePdfPayload(invoice, settings, reglements),
            open: true,
        });
    };

    useEffect(() => {
        let isMounted = true;

        if (!showPrintSettingsModal || !invoiceToPrint) {
            return undefined;
        }

        (async () => {
            const reglements = await loadInvoicePayments(invoiceToPrint);
            const previewUrl = await generateDoc("facture", {
                ...buildInvoicePdfPayload(invoiceToPrint, printSettings, reglements),
                open: false,
                returnBlobUrl: true,
            });

            if (!isMounted) {
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                return;
            }

            setPrintPreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return previewUrl;
            });
        })();

        return () => {
            isMounted = false;
        };
    }, [buildInvoicePdfPayload, invoiceToPrint, loadInvoicePayments, printSettings, showPrintSettingsModal]);

    const openPrintSettings = (invoice) => {
        setInvoiceToPrint(invoice);
        setPrintPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        setPrintSettings({
            scale: 100,
            offsetX: 0,
            offsetY: 0,
            showPaymentDetails: true,
        });
        setShowPrintSettingsModal(true);
    };

    const handleConfirmPrint = async () => {
        if (!invoiceToPrint) return;
        await handleGeneratePdf(invoiceToPrint, printSettings);
        setShowPrintSettingsModal(false);
        setPrintPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        setInvoiceToPrint(null);
    };

    // Fonction pour obtenir le badge de statut
    const getStatusBadge = (statut) => {
        switch (statut) {
            case 'BROUILLON':
                return <Badge color="secondary"><FaClock className="me-1" />{t('facture.status.draft')}</Badge>;
            case 'IMPAYE':
                return <Badge color="secondary"><FaClock className="me-1" />{t('facture.unpaid')}</Badge>;
            case 'VALIDEE':
                return <Badge color="info"><FaCheckCircle className="me-1" />{t('facture.validated')}</Badge>;
            case 'PAYEE':
                return <Badge color="success"><FaCheck className="me-1" />{t('facture.paid')}</Badge>;
            case 'PARTIELLEMENT_PAYEE':
                return <Badge color="warning"><FaExclamationTriangle className="me-1" />{t('facture.partiallyPaid')}</Badge>;
            case 'ANNULEE':
                return <Badge color="danger"><FaTimesCircle className="me-1" />{t('facture.cancelled')}</Badge>;
            case 'EN_RETARD':
                return <Badge color="danger"><FaClock className="me-1" />{t('facture.status.overdue')}</Badge>;
            default:
                return <Badge color="light">{statut}</Badge>;
        }
    };

    const clearFilters = () => {
        const nextFilters = {
            query: '',
            clientId: null,
            dateFrom: formatDate(startOfCurrentMonth),
            dateTo: formatDate(tomorrowDate),
            amount: null,
            month: '',
        };
        setAmountInput('');
        setCurrentPage(0);
        setFilters(nextFilters);
        fetchInvoicesData({
            ...nextFilters,
            page: 0,
            size: pageSize
        });
    };

    const handleCloseInvoiceModal = () => {
        setShowInvoiceModal(false);
        setInvoice({
            lignes: [],
            dateFacture: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
            dateEcheance: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
            statut: 'IMPAYE'
        });
        fetchInvoicesData({
            ...filters,
            page: currentPage,
            size: pageSize
        });
    };

    const visiblePages = useMemo(() => {
        if (!totalPages || totalPages <= 0) return [];
        const start = Math.max(0, currentPage - 2);
        const end = Math.min(totalPages - 1, currentPage + 2);
        const pages = [];
        for (let i = start; i <= end; i += 1) {
            pages.push(i);
        }
        return pages;
    }, [currentPage, totalPages]);

    const handlePageChange = (page) => {
        if (page < 0 || page >= totalPages || page === currentPage) return;
        setCurrentPage(page);
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaAngleUp className="ms-1 text-muted" />;
        return sortConfig.direction === 'asc'
            ? <FaAngleUp className="ms-1" />
            : <FaAngleDown className="ms-1" />;
    };

    const displayedInvoices = useMemo(() => {
        const toNumber = (value) => {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : 0;
        };
        const toDateValue = (value) => {
            if (!value) return 0;
            const parsed = new Date(value);
            const time = parsed.getTime();
            return Number.isFinite(time) ? time : 0;
        };

        const safeList = Array.isArray(invoices) ? invoices.filter(Boolean) : [];

        return [...safeList].sort((a, b) => {
            let compare = 0;
            if (sortConfig.key === 'numero') {
                compare = (a?.numero || '').toString().localeCompare((b?.numero || '').toString(), 'fr', { sensitivity: 'base' });
            } else if (sortConfig.key === 'clientNom') {
                compare = (a?.clientNom || '').toString().localeCompare((b?.clientNom || '').toString(), 'fr', { sensitivity: 'base' });
            } else if (sortConfig.key === 'dateFacture') {
                compare = toDateValue(a?.dateFacture) - toDateValue(b?.dateFacture);
            } else if (sortConfig.key === 'montantTotalTTC') {
                compare = toNumber(a?.montantTotalTTC) - toNumber(b?.montantTotalTTC);
            } else if (sortConfig.key === 'montantRegle') {
                compare = toNumber(a?.montantRegle) - toNumber(b?.montantRegle);
            }
            return sortConfig.direction === 'asc' ? compare : -compare;
        });
    }, [invoices, sortConfig]);

    // Handle creating an invoice from selected deliveries
    const handleCreateInvoice = () => {
        setShowInvoiceModal(true);
    };

    const handleDownloadListPdf = () => {
        try {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 14;

            // --------- Header
            const title = t('facture.list') || 'Liste des factures';
            const period =
                `${t('common.from') || 'Du'} ${filters.dateFrom || '-'} ` +
                `${t('common.to') || 'au'} ${filters.dateTo || '-'}`;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.text(title, margin, 18);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(period, margin, 26);

            // Company (if available)
            if (company?.name) {
                const right = pageWidth - margin;
                doc.setFontSize(9);
                doc.text(company.name, right, 12, { align: 'right' });
                if (company.address) doc.text(company.address, right, 16, { align: 'right' });
                if (company.email || company.phone1) {
                    doc.text(
                        `${company.email ? company.email : ''}${company.email && company.phone1 ? ' | ' : ''}${company.phone1 || ''}`,
                        right, 20, { align: 'right' }
                    );
                }
            }

            // --------- Table data
            const tableBody = (invoices || []).map(inv => ([
                inv.numero || '',
                inv.clientNom || '',
                inv.dateFacture || '',
                inv.statut || '',
                inv.modeReglement || '',
                formatPrice(inv.montantTotalTTC || 0, false),
                formatPrice(inv.montantTotalHT || 0, false),
                formatPrice(inv.montantTotalTaxe || 0, false),
            ]));

            autoTable(doc, {
                startY: 32,
                head: [[
                    t('facture.table.invoiceNb') || 'N° Facture',
                    t('facture.client') || 'Client',
                    t('facture.table.Date') || 'Date',
                    t('facture.table.status') || 'Statut',
                    t('facture.modeReglement') || 'Mode règlement',
                    t('facture.table.totalTtc') || 'Total TTC',
                    t('facture.table.totalHt') || 'Total HT',
                    t('reception.totalTva') || 'TVA'
                ]],
                body: tableBody,
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: {
                    fillColor: [52, 73, 94],
                    textColor: 255,
                    fontStyle: 'bold',
                    valign: 'middle', // vertical centering
                },
                columnStyles: {
                    0: { cellWidth: 40, halign: 'center', headerHalign: 'center' },  // Invoice #
                    1: { cellWidth: 55, halign: 'left', headerHalign: 'left' },      // Client
                    2: { cellWidth: 24, halign: 'center', headerHalign: 'center' },  // Date
                    3: { cellWidth: 28, halign: 'center', headerHalign: 'center' },  // Status
                    4: { cellWidth: 30, halign: 'center', headerHalign: 'center' },  // Mode règlement
                    5: { cellWidth: 28, halign: 'right', headerHalign: 'right' },    // Total TTC
                    6: { cellWidth: 28, halign: 'right', headerHalign: 'right' },    // Total HT
                    7: { cellWidth: 24, halign: 'right', headerHalign: 'right' },    // TVA
                },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                margin: { left: 12, right: 12 },
                didDrawPage: (data) => {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();
                    const str = `${t('common.page') || 'Page'} ${doc.internal.getNumberOfPages()}`;
                    doc.setFontSize(8);
                    doc.text(str, pageWidth / 2, pageHeight - 6, { align: 'center' });
                },
            });


            // --------- Summary block
            const list = invoices || [];
            const sum = (arr, sel) => arr.reduce((s, it) => s + (sel(it) || 0), 0);
            const totalHT = sum(list, x => x.montantTotalHT);
            const totalTVA = sum(list, x => x.montantTotalTaxe);
            const totalTTC = sum(list, x => x.montantTotalTTC);
            const count = list.length;
            const avgTTC = count ? (totalTTC / count) : 0;

            // group by status
            const byStatus = list.reduce((acc, it) => {
                const k = it.statut || 'N/A';
                if (!acc[k]) acc[k] = { count: 0, ttc: 0 };
                acc[k].count += 1;
                acc[k].ttc += (it.montantTotalTTC || 0);
                return acc;
            }, {});

            let y = doc.lastAutoTable.finalY + 8;
            const boxW = pageWidth - margin * 2;
            doc.setFillColor(245, 245, 245);
            doc.roundedRect(margin, y, boxW, 40, 2, 2, 'F');
            doc.setDrawColor(220, 220, 220);
            doc.roundedRect(margin, y, boxW, 40, 2, 2, 'S');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(30);
            doc.text(t('common.summary') || 'Résumé', margin + 4, y + 7);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(60);

            // left col
            const leftX = margin + 4;
            const midX = margin + boxW / 2 + 4;
            let ly = y + 15;

            doc.text(`${t('common.count') || 'Nb factures'}: ${count}`, leftX, ly); ly += 6;
            doc.text(`${t('facture.table.totalHt') || 'Total HT'}: ${formatPrice(totalHT, false)}`, leftX, ly); ly += 6;
            doc.text(`${t('reception.totalTva') || 'TVA'}: ${formatPrice(totalTVA, false)}`, leftX, ly); ly += 6;
            doc.setFont('helvetica', 'bold');
            doc.text(`${t('facture.table.totalTtc') || 'Total TTC'}: ${formatPrice(totalTTC, false)}`, leftX, ly);

            // right col (status breakdown)
            doc.setFont('helvetica', 'normal');
            let ry = y + 15;
            doc.text(t('facture.table.status') || 'Statuts', midX, ry); ry += 5;
            Object.entries(byStatus).forEach(([st, v]) => {
                doc.text(`• ${st}: ${v.count} — ${formatPrice(v.ttc, false)}`, midX, ry);
                ry += 5;
            });

            // --------- Save / Open
            const filename = `Factures_${filters.dateFrom || ''}_${filters.dateTo || ''}.pdf`;
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 1200);
            if (!win) doc.save(filename);
        } catch (e) {
            console.error('PDF export error', e);
            Swal.fire({
                icon: 'error',
                title: t('common.error') || 'Erreur',
                text: t('facture.pdfError') || 'Erreur lors de la génération du PDF.',
            });
        }
    };

    return (
        <Container fluid>
            {/* En-tête avec statistiques */}
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaFileInvoice className="text-primary me-2" />
                                {t('facture.list')}
                            </h4>
                            <div className="d-flex flex-wrap align-items-center gap-3">
                                <div className="d-flex align-items-center gap-3 small">
                                    <div>
                                        <div className="text-muted">{t('facture.table.totalTtc')}</div>
                                        <div className="fw-bold">{formatPrice(totalTtcSummary, false)}</div>
                                    </div>
                                   
                                    <div>
                                        <div className="text-muted">{t('facture.montantRegle')}</div>
                                        <div className="fw-bold">{formatPrice(totalRegleSummary, false)}</div>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        color="primary"
                                        onClick={handleDownloadListPdf}
                                        size="sm"
                                        className="d-flex align-items-center"
                                    >
                                        <FaFilePdf className="me-2" size={14} />
                                        {t('facture.downloadListPdf') || 'Export PDF'}
                                    </Button>

                                    <Button
                                        color="success"
                                        onClick={handleExportInvoicesExcel}
                                        size="sm"
                                        className="d-flex align-items-center"
                                        title={t('facture.exportExcel') || 'Exporter Excel'}
                                    >
                                        <FaFileExcel className="me-2" size={14} />
                                        Excel
                                    </Button>
                                </div>
                            </div>

                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm mb-4">
                <CardBody>
                    <Row className="mb-3 g-2 align-items-end">
                        <Col lg={2} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.search')}</Label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <Input
                                        type="text"
                                        innerRef={searchInputRef}
                                        placeholder={t('facture.searchPlaceholder')}
                                        value={filters.query}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
                                            setFilters(prevFilters => ({
                                                ...prevFilters,
                                                query: value
                                            }));
                                        }}
                                        className="text-uppercase"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch(e.target.value.toUpperCase());
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">Montant</Label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaMoneyBillWave />
                                    </span>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        placeholder="Ex: 1200"
                                        className="no-number-spinner"
                                        value={amountInput}
                                        onChange={e => {
                                            setAmountInput(e.target.value);
                                        }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                const value = e.target.value;
                                                setCurrentPage(0);
                                                setFilters(prevFilters => ({
                                                    ...prevFilters,
                                                    amount: value === '' ? null : value
                                                }));
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label htmlFor="clientId">{t('bonLivraison.client')}:</Label>
                                <Select
                                    id="clientId"
                                    options={clientOptionsWithAll}
                                    value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                                    onChange={(selectedOption) => {
                                        setCurrentPage(0);
                                        setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null }));
                                    }}
                                    placeholder={t('bonLivraison.selectClient')}
                                    isSearchable
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="month" className="mb-1">{t('common.month', 'Mois')}</Label>
                                <Input
                                    type="month"
                                    id="month"
                                    value={filters.month}
                                    onChange={e => {
                                        const month = e.target.value;
                                        const { dateFrom, dateTo } = getMonthDateRange(month);
                                        setCurrentPage(0);
                                        setFilters(f => ({
                                            ...f,
                                            month,
                                            dateFrom,
                                            dateTo,
                                        }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateFrom" className="mb-1">Date début</Label>
                                <Input
                                    type="date"
                                    id="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={e => {
                                        setCurrentPage(0);
                                        setFilters(f => ({ ...f, month: '', dateFrom: e.target.value }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateTo" className="mb-1">Date fin</Label>
                                <Input
                                    type="date"
                                    id="dateTo"
                                    value={filters.dateTo}
                                    onChange={e => {
                                        setCurrentPage(0);
                                        setFilters(f => ({ ...f, month: '', dateTo: e.target.value }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        {/* <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                            <Button
                                color="secondary"
                                onClick={clearFilters}
                                title={t('common.clearFilters')}
                                className="mb-3"
                                style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <FaFilter />
                            </Button>
                        </Col> */}
                    </Row>
                </CardBody>
            </Card>

            {/* Tableau des factures */}
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <table className="table table-hover table-striped table-bordered" style={{ fontSize: '0.8rem' }}>
                                <thead className="thead-light">
                                    <tr>
                                        <th
                                            className="align-middle text-center py-2"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleSort('numero')}
                                        >
                                            {t('facture.table.invoiceNb')}
                                            {renderSortIcon('numero')}
                                        </th>
                                        <th
                                            className="align-middle text-center"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleSort('clientNom')}
                                        >
                                            {t('facture.client')}
                                            {renderSortIcon('clientNom')}
                                        </th>
                                        <th
                                            className="align-middle text-center"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleSort('dateFacture')}
                                        >
                                            {t('facture.table.Date')}
                                            {renderSortIcon('dateFacture')}
                                        </th>
                                        <th
                                            className="align-middle text-center"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleSort('montantTotalTTC')}
                                        >
                                            {t('facture.table.totalTtc')}
                                            {renderSortIcon('montantTotalTTC')}
                                        </th>
                                        <th className="align-middle text-center">{t('facture.table.totalHt')}</th>
                                        <th className="align-middle text-center">{t('reception.totalTva')}</th>
                                        <th
                                            className="align-middle text-center"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleSort('montantRegle')}
                                        >
                                            {t('facture.montantRegle')}
                                            {renderSortIcon('montantRegle')}
                                        </th>
                                        <th className="align-middle text-center">{t('facture.table.blNumbers')}</th>
                                        <th className="align-middle text-center">{t('facture.table.status')}</th>
                                        <th className="align-middle text-center" style={{ width: '150px' }}>{t('facture.table.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedInvoices?.map((invoice) => {
                                        const bonLivraisonRefs = invoice.bonLivraionsRefs
                                            ? invoice.bonLivraionsRefs.split(',').map(receipt => receipt.trim()).filter(Boolean)
                                            : [];
                                        const displayedBonLivraisonRefs = bonLivraisonRefs.length > 3
                                            ? [...bonLivraisonRefs.slice(0, 2), '...']
                                            : bonLivraisonRefs;
                                        return (
                                            <tr key={invoice.id}>
                                                <td className="align-middle text-center py-1">{invoice.numero}</td>
                                                <td className="align-middle text-center py-1">{invoice?.clientNom}</td>
                                                <td className="align-middle text-center py-1">{invoice.dateFacture}</td>
                                                <td className="align-middle text-center">{formatPrice(invoice.montantTotalTTC, false)}</td>
                                                <td className="align-middle text-center">{formatPrice(invoice.montantTotalHT, false)}</td>
                                                <td className="align-middle text-center">{formatPrice(invoice.montantTotalTaxe, false)}</td>
                                                <td className="align-middle text-center">{formatPrice(invoice.montantRegle, false)}</td>
                                                <td className="align-middle text-center">
                                                    {bonLivraisonRefs.length > 0 ? (
                                                        <div className="d-flex flex-column align-items-center">
                                                            {displayedBonLivraisonRefs.map((receipt, index) => (
                                                                <Badge key={index} color="success" className='mb-1' style={{ fontSize: '10px' }}>
                                                                    {receipt}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">Aucun</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {getStatusBadge(invoice.statut)}
                                                </td>

                                                <td className="align-middle text-center">
                                                    <div className="btn-group">
                                                        {/* Bouton PDF */}
                                                        <Button
                                                            color="secondary"
                                                            title={t('facture.downloadPdf')}
                                                            onClick={() => openPrintSettings(invoice)}
                                                            size="sm"
                                                            className="me-1"
                                                        >
                                                            <FaFilePdf size={12} />
                                                        </Button>

                                                        {/* Bouton Éditer */}
                                                        <Button
                                                            color="warning"
                                                            title={t('facture.edit')}
                                                            onClick={() => handleEditInvoice(invoice)}
                                                            size="sm"
                                                            className="me-1"
                                                        //disabled={!canEditInvoice(invoice)}
                                                        >
                                                            <FaEdit size={12} />
                                                        </Button>

                                                        {invoice.statut !== 'ANNULEE' && <Button
                                                            color="danger"
                                                            title={t('commande.cancel')}
                                                            onClick={() => handleAnnulerFacture(invoice)}
                                                            className='me-1'
                                                            size="sm"
                                                        >
                                                            <FaTrash />
                                                        </Button>}

                                                        <Button
                                                            color="info"
                                                            title={t('payment.payments', 'Paiements')}
                                                            onClick={() => openPaymentsModal(invoice)}
                                                            className='me-1'
                                                            size="sm"
                                                            disabled={!invoice.numero}
                                                        >
                                                            <FaMoneyBillWave size={12} />
                                                        </Button>

                                                        {/* Menu déroulant pour plus d'actions */}
                                                        {/* <UncontrolledDropdown>
                                                            <DropdownToggle 
                                                                caret 
                                                                color="light" 
                                                                size="sm"
                                                                className="border"
                                                            >
                                                                <FaEllipsisV size={10} />
                                                            </DropdownToggle>
                                                            <DropdownMenu>
                                                                <DropdownItem 
                                                                    onClick={() => console.log('Duplicate invoice', invoice.id)}
                                                                >
                                                                    <FaCopy className="me-2" />
                                                                    {t('facture.duplicate')}
                                                                </DropdownItem>
                                                                
                                                                {invoice.statut !== 'PAYEE' && (
                                                                    <DropdownItem 
                                                                        onClick={() => console.log('Mark as paid', invoice.id)}
                                                                    >
                                                                        <FaMoneyBillWave className="me-2" />
                                                                        {t('facture.markAsPaid')}
                                                                    </DropdownItem>
                                                                )}

                                                                {invoice.statut !== 'ANNULEE' && (
                                                                    <DropdownItem 
                                                                        onClick={() => console.log('Cancel invoice', invoice.id)}
                                                                    >
                                                                        <FaTimes className="me-2" />
                                                                        {t('facture.cancel')}
                                                                    </DropdownItem>
                                                                )}

                                                                <DropdownItem divider />
                                                                
                                                                <DropdownItem 
                                                                    onClick={() => handleDeleteInvoice(invoice)}
                                                                    disabled={!canDeleteInvoice(invoice)}
                                                                    className="text-danger"
                                                                >
                                                                    <FaTrash className="me-2" />
                                                                    {t('facture.delete')}
                                                                </DropdownItem>
                                                            </DropdownMenu>
                                                        </UncontrolledDropdown> */}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {(!displayedInvoices || displayedInvoices.length === 0) && (
                                        <tr>
                                            <td colSpan="13" className="text-center py-4">
                                                <div className="text-muted">{t('facture.noInvoices')}</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-light fw-bold">
                                        <td colSpan="3" className="text-end">{t('common.totals')}</td>
                                        <td className="text-center">{formatPrice(invoices.reduce((sum, invoice) => sum + (invoice.montantTotalTTC || 0), 0), false)}</td>
                                        <td className="text-center">{formatPrice(invoices.reduce((sum, invoice) => sum + (invoice.montantTotalHT || 0), 0), false)}</td>
                                        <td className="text-center">{formatPrice(invoices.reduce((sum, invoice) => sum + (invoice.montantTotalTaxe || 0), 0), false)}</td>
                                        <td className="align-middle text-center">{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantRegle, 0), false)}</td>
                                        <td colSpan="4"></td>
                                    </tr>
                                </tfoot>
                            </table>
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                                <div className="d-flex align-items-center gap-2">
                                    <Label for="pageSize" className="mb-0">Taille page</Label>
                                    <Input
                                        id="pageSize"
                                        type="select"
                                        value={pageSize}
                                        onChange={(e) => {
                                            const nextSize = Number(e.target.value);
                                            setPageSize(nextSize);
                                            setCurrentPage(0);
                                        }}
                                        style={{ width: '90px' }}
                                    >
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </Input>
                                    <span className="text-muted small">
                                        {totalElements || 0} element(s)
                                    </span>
                                </div>
                                <Pagination size="sm" className="mb-0">
                                    <PaginationItem disabled={currentPage <= 0}>
                                        <PaginationLink first onClick={() => handlePageChange(0)} />
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPage <= 0}>
                                        <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                                    </PaginationItem>
                                    {visiblePages.map((page) => (
                                        <PaginationItem key={page} active={page === currentPage}>
                                            <PaginationLink onClick={() => handlePageChange(page)}>
                                                {page + 1}
                                            </PaginationLink>
                                        </PaginationItem>
                                    ))}
                                    <PaginationItem disabled={totalPages <= 0 || currentPage >= totalPages - 1}>
                                        <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                                    </PaginationItem>
                                    <PaginationItem disabled={totalPages <= 0 || currentPage >= totalPages - 1}>
                                        <PaginationLink last onClick={() => handlePageChange(totalPages - 1)} />
                                    </PaginationItem>
                                </Pagination>
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={showPaymentsModal} toggle={closePaymentsModal} size="lg">
                <ModalHeader toggle={closePaymentsModal}>
                    {t('payment.payments', 'Paiements')} - {selectedInvoiceForPayments?.numero}
                </ModalHeader>
                <ModalBody>
                    {selectedInvoiceForPayments && (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <div className="text-muted small">{t('facture.client', 'Client')}</div>
                                    <div className="fw-bold">{selectedInvoiceForPayments.clientNom || '-'}</div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-muted small">{t('facture.table.totalTtc', 'Total TTC')}</div>
                                    <div className="fw-bold">{formatPrice(selectedInvoiceForPayments.montantTotalTTC, false)}</div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-muted small">{t('payment.remainingAmount', 'Reste à payer')}</div>
                                    <div className={`fw-bold ${selectedInvoiceRemainingAmount > 0 ? 'text-danger' : 'text-success'}`}>
                                        {formatPrice(selectedInvoiceRemainingAmount, false)}
                                    </div>
                                </Col>
                            </Row>

                            {paymentsLoadingKey === selectedInvoicePaymentKey ? (
                                <div className="text-center text-muted py-4">
                                    <Spinner size="sm" className="me-2" />
                                    {t('common.loading', 'Chargement...')}
                                </div>
                            ) : paymentsError ? (
                                <div className="text-center text-danger py-4">
                                    {t('payment.loadingError', 'Impossible de charger les paiements')}
                                </div>
                            ) : selectedInvoicePayments.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    {t('payment.noPayments', 'Aucun paiement')}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm table-striped table-bordered mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t('common.reference', 'Référence')}</th>
                                                <th>{t('payment.dateReglement', 'Date règlement')}</th>
                                                <th>{t('payment.mode', 'Mode')}</th>
                                                <th>{t('payment.piece', 'Pièce')}</th>
                                                <th>{t('payment.dateEcheance', 'Échéance')}</th>
                                                <th>{t('payment.banque', 'Banque')}</th>
                                                <th className="text-end">{t('payment.amount', 'Montant')}</th>
                                                <th className="text-end">{t('payment.remainingAmount', 'Reste')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedInvoicePayments.map((payment, index) => (
                                                <tr key={payment.id || `${payment.refReglement || 'payment'}-${index}`}>
                                                    <td>{payment.refReglement || '-'}</td>
                                                    <td>{formatDateFr(payment.dateReglement || payment.createdAt)}</td>
                                                    <td>{getPaymentModeLabel(payment.modeReglement)}</td>
                                                    <td>{payment.numeroCheque || '-'}</td>
                                                    <td>{formatDateFr(payment.dateEcheance)}</td>
                                                    <td>{payment.banque || '-'}</td>
                                                    <td className="text-end fw-bold">{formatPrice(payment.montantAlloue ?? payment.montant ?? payment.montantRegle, false)}</td>
                                                    <td className="text-end">{formatPrice(payment.montantRestant, false)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan="6" className="text-end fw-bold">{t('payment.totalPaid', 'Total payé')}:</td>
                                                <td className="text-end fw-bold">{formatPrice(selectedInvoicePaymentsTotal, false)}</td>
                                                <td className="text-end fw-bold">{formatPrice(selectedInvoiceRemainingAmount, false)}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closePaymentsModal}>
                        {t('common.close', 'Fermer')}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Modal pour la gestion des sous-factures */}
            <Modal
                isOpen={showSubInvoiceModal}
                toggle={() => setShowSubInvoiceModal(false)}
                size="xl"
                className="modal-fullscreen-lg-down"
            >
                <ModalHeader toggle={() => setShowSubInvoiceModal(false)}>
                    {t('subInvoice.manageSubInvoices')} - {selectedInvoiceForSubInvoices?.numero}
                </ModalHeader>
                <ModalBody>
                    {selectedInvoiceForSubInvoices && (
                        <SubInvoiceManager
                            parentInvoice={selectedInvoiceForSubInvoices}
                            existingSubInvoices={getSubInvoicesForInvoice(selectedInvoiceForSubInvoices.id)}
                            onSubInvoicesCreated={handleSubInvoicesCreation}
                            onClose={() => setShowSubInvoiceModal(false)}
                        />
                    )}
                </ModalBody>
            </Modal>

            {/* Modal pour l'édition des factures */}
            <Modal
                isOpen={showEditModal}
                toggle={handleCloseEditModal}
                size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseEditModal}>
                    {t('facture.editInvoice')} - {selectedInvoiceForEdit?.numero}
                </ModalHeader>
                <ModalBody>
                    {selectedInvoiceForEdit && (
                        <ViewFacture
                            invoice={selectedInvoiceForEdit}
                            onSave={handleSaveInvoice}
                            onCancel={handleCloseEditModal}
                            isEditing={true}
                        />
                    )}
                </ModalBody>
            </Modal>
            <Modal isOpen={showInvoiceModal} toggle={handleCloseInvoiceModal} size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}>
                <ModalHeader toggle={handleCloseInvoiceModal}>
                    {t('facture.newInvoice')}
                </ModalHeader>
                <ModalBody>
                    <InvoiceForm
                        initialValues={invoice}
                        onClose={handleCloseInvoiceModal}
                    />
                </ModalBody>
            </Modal>
            <Modal
                isOpen={showPrintSettingsModal}
                toggle={() => {
                    setShowPrintSettingsModal(false);
                    setPrintPreviewUrl((prev) => {
                        if (prev) URL.revokeObjectURL(prev);
                        return null;
                    });
                    setInvoiceToPrint(null);
                }}
                size="xl"
            >
                <ModalHeader toggle={() => {
                    setShowPrintSettingsModal(false);
                    setPrintPreviewUrl((prev) => {
                        if (prev) URL.revokeObjectURL(prev);
                        return null;
                    });
                    setInvoiceToPrint(null);
                }}>
                    {t('facture.downloadPdf', 'Imprimer la facture')}
                </ModalHeader>
                <ModalBody>
                    <Row className="g-3">
                        <Col md={4}>
                            <FormGroup>
                                <Label for="printShowPaymentDetails">
                                    {t('payment.printOptions', 'Options paiements')}
                                </Label>
                                <div className="d-flex align-items-center gap-2">
                                    <Input
                                        id="printShowPaymentDetails"
                                        type="checkbox"
                                        checked={printSettings.showPaymentDetails}
                                        onChange={(e) => setPrintSettings((prev) => ({ ...prev, showPaymentDetails: e.target.checked }))}
                                    />
                                    <Label for="printShowPaymentDetails" className="mb-0">
                                        {t('payment.printWithDetails', 'Imprimer avec détails paiements')}
                                    </Label>
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label for="printScale">Echelle: {printSettings.scale}%</Label>
                                <Input
                                    id="printScale"
                                    type="range"
                                    min="70"
                                    max="100"
                                    step="1"
                                    value={printSettings.scale}
                                    onChange={(e) => setPrintSettings((prev) => ({ ...prev, scale: Number(e.target.value) }))}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="printOffsetX">Deplacement horizontal: {printSettings.offsetX}px</Label>
                                <Input
                                    id="printOffsetX"
                                    type="range"
                                    min="-80"
                                    max="80"
                                    step="1"
                                    value={printSettings.offsetX}
                                    onChange={(e) => setPrintSettings((prev) => ({ ...prev, offsetX: Number(e.target.value) }))}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="printOffsetY">Deplacement vertical: {printSettings.offsetY}px</Label>
                                <Input
                                    id="printOffsetY"
                                    type="range"
                                    min="-80"
                                    max="80"
                                    step="1"
                                    value={printSettings.offsetY}
                                    onChange={(e) => setPrintSettings((prev) => ({ ...prev, offsetY: Number(e.target.value) }))}
                                />
                            </FormGroup>
                            <div className="text-muted small">
                                Reduisez l'echelle pour essayer de faire tenir le contenu sur une seule page, puis ajustez la position si necessaire.
                            </div>
                        </Col>
                        <Col md={8}>
                            <div className="border rounded overflow-hidden" style={{ height: '70vh', backgroundColor: '#f8f9fa' }}>
                                {printPreviewUrl ? (
                                    <iframe
                                        title="invoice-print-preview"
                                        src={printPreviewUrl}
                                        style={{ width: '100%', height: '100%', border: 0 }}
                                    />
                                ) : (
                                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                                        Generation de l'aperçu...
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => {
                        setShowPrintSettingsModal(false);
                        setPrintPreviewUrl((prev) => {
                            if (prev) URL.revokeObjectURL(prev);
                            return null;
                        });
                        setInvoiceToPrint(null);
                    }}>
                        {t('common.cancel')}
                    </Button>
                    <Button color="primary" onClick={handleConfirmPrint}>
                        {t('facture.downloadPdf', 'Ouvrir le PDF')}
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default InvoiceList;


